/**
 * ChatPanel — LLM 채팅 패널 (독립 SolaView 컴포넌트)
 *
 * DealDetailView 의 채팅 섹션에서 승격 (정리 Step 5, 2026-07-03 — 분해 계획 v2 §1).
 * 마크업·CSS 클래스·동작은 기존 renderChatSection + 채팅 핸들러 그대로.
 *
 * ─── 컴포넌트 계약 (Sola 관례 — Pager/DataSheet 와 동일 패턴) ─────────
 *
 *   부모:
 *     private readonly chat = new ChatPanel({
 *       send:   (content, atts) => api.postDealChat(dealId, content, atts),
 *       onSent: async () => {  부모 refetch → this.chat.setMessages(...)  },
 *     });
 *     render 템플릿에 ${this.chat} 로 임베드 (htmlSolaWithInstance 가 mount/destroy 관리)
 *     메시지 갱신은 부모가 chat.setMessages(messages) 호출 (props 하향 전달)
 *
 *   소유권:
 *     - 입력/첨부/전송중/에러/optimistic pending = 컴포넌트 내부 state
 *     - 메시지 목록 = 부모 소유 (서버 refetch 결과) — setMessages 로만 수신
 *     - API 호출은 send 콜백 주입 — 엔드포인트를 몰라서 딜 외 컨텍스트에서 재사용 가능
 *
 *   전송 흐름 (11-4 optimistic UX 유지):
 *     전송 클릭 → pending user row + spinner 즉시 표시 → send() →
 *     onSent() (부모 refetch, 끝날 때까지 spinner 유지) → pending 제거
 */

import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import { escapeHtml, fmtDateTime } from '../lib/cells';

export interface ChatPanelOptions {
  /** 메시지 전송 — 컨텍스트별 API 를 주입 (딜 상세: postDealChat). */
  send: (content: string, attachments: api.ChatAttachmentFile[]) => Promise<void>;
  /**
   * 전송 성공 직후 — 부모의 refetch/화면 갱신.
   * 이 Promise 가 끝날 때까지 spinner 유지 (기존 동작: refetch 완료까지 '답변 생성중').
   */
  onSent?: () => Promise<void> | void;
}

interface ChatPanelData {
  messages:        api.ChatMessage[];             // 부모 공급 (시간순 정렬은 여기서)
  chatInput:       string;
  chatSending:     boolean;
  chatError:       string;
  chatAttachments: api.ChatAttachmentFile[];      // 7-3: 첨부 이미지

  // 11-4 UX: 사용자 메시지 즉시 표시 (optimistic).
  // 전송 직후 chatPending 채워서 UI 에 user 메시지 + 첨부 미리보기 바로 그림.
  // 부모 refetch 로 진짜 chat_message 가 들어오면 null.
  chatPending: null | {
    content:     string;
    attachments: Array<{ name: string; isImage: boolean; previewUrl: string }>;
    at:          string;     // ISO datetime
  };
}

/** 채팅 메시지 발신자 라벨 (P8-7 패턴). */
function renderMsgSender(m: api.ChatMessage): string {
  if (m.role === 'assistant') return '🤖 봇';
  const name = m.slack_user_name?.trim();
  return name ? `🧑 ${escapeHtml(name)}` : '🧑 사용자';
}

/**
 * 메시지의 첨부 이미지들을 thumbnail 로 표시.
 * attachments JSONB 의 각 item — { url, name, mime, size }
 *   url  = R2 또는 Slack URL (P9 11-2 + migration 088 으로 slack_url 정리됨)
 *   mime 이 image/* 면 thumbnail, 아니면 파일 link
 * 클릭 시 새 탭으로 원본 열기.
 */
function renderMsgAttachments(m: api.ChatMessage): string {
  const atts = (m.attachments ?? null) as Array<{
    url?:       string;
    name?:      string;
    mime?:      string | null;
    size?:      number | null;
  }> | null;
  if (!atts || atts.length === 0) return '';

  const html = atts.map((att) => {
    const url  = att.url ?? '';
    const name = att.name ?? '첨부';
    const mime = att.mime ?? '';
    if (!url) return '';

    if (mime.startsWith('image/')) {
      return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener" class="deal-chat-msg-attachment-image-link">
                <img src="${escapeHtml(url)}" alt="${escapeHtml(name)}"
                     class="deal-chat-msg-attachment-image" loading="lazy" />
              </a>`;
    }
    // 이미지 아닌 첨부 — 링크
    return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener"
               class="deal-chat-msg-attachment-link">📎 ${escapeHtml(name)}</a>`;
  }).join('');

  return `<div class="deal-chat-msg-attachments">${html}</div>`;
}

export class ChatPanel extends SolaView<ChatPanelData> {
  override events = {
    click: {
      '@chat-send':          'handleChatSend',
      '@chat-attach':        'handleChatAttachClick',
      '@chat-attach-remove': 'handleChatAttachRemove',
    },
    input:   { '@chat-input': 'handleChatInputChange' },
    keydown: { '@chat-input': 'handleChatKeydown' },
    paste:   { '@chat-input': 'handleChatPaste' },
    change:  { '@chat-file-input': 'handleChatFileSelect' },
  };

  private readonly opts: ChatPanelOptions;

  constructor(opts: ChatPanelOptions) {
    super();
    this.opts = opts;
    this.data = {
      messages:        [],
      chatInput:       '',
      chatSending:     false,
      chatError:       '',
      chatAttachments: [],
      chatPending:     null,
    };
  }

  // ─── 부모 → 컴포넌트: 메시지 공급 ───────────────────────────────
  /** 부모의 refetch 결과 반영. 마운트 전엔 저장만 (render 가 사용). */
  setMessages(messages: api.ChatMessage[]): void {
    this.data.messages = messages;
    if (this._rootElement) {
      this.refreshInner();
    }
  }

  // ─── 렌더 ────────────────────────────────────────────────────────
  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <section class="deal-side-section deal-chat-section">${this.renderInner()}</section>
    `;
  }

  override onMount(): void {
    this.scrollToBottom();
    this.syncTextareaHeight();
  }

  /** 섹션 내부만 다시 그림 — 섹션 요소 자체는 안정 (이벤트 위임 루트 유지). */
  private refreshInner(): void {
    const root = this._rootElement;
    if (!root) return;
    root.innerHTML = this.renderInner();
    this.scrollToBottom();
    this.syncTextareaHeight();     // 재렌더 후 textarea 높이 복원(전송 후 1줄로 리셋 포함)
  }

  /** 채팅 메시지 list 의 scrollTop = scrollHeight (최신 메시지 보임). */
  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      const list = this._rootElement?.querySelector<HTMLElement>('.deal-chat-list');
      if (list) list.scrollTop = list.scrollHeight;
    });
  }

  private renderInner(): string {
    // 모든 thread 의 메시지 시간순 (부모가 평탄화해서 전달, 정렬은 여기서 보장)
    const allMessages = [...this.data.messages];
    allMessages.sort((a, b) => a.created_at.localeCompare(b.created_at));

    const { chatInput, chatSending, chatError, chatAttachments, chatPending } = this.data;
    const sendDisabled = chatSending || (!chatInput.trim() && chatAttachments.length === 0);

    // 첨부 미리보기 (썸네일 또는 파일 아이콘 + 제거 버튼)
    //   image/*  → 썸네일 (이미지 미리보기)
    //   xlsx/csv → 📊 아이콘 + 파일명
    const attsPreview = chatAttachments.length > 0
      ? `<div class="deal-chat-attach-preview">${chatAttachments.map((att, i) => {
          const isImage = att.file.type.startsWith('image/');
          const inner = isImage
            ? `<img src="${att.previewUrl}" alt="${escapeHtml(att.file.name)}" />`
            : `<div class="deal-chat-attach-file" title="${escapeHtml(att.file.name)}">
                 <span class="deal-chat-attach-file-icon">📊</span>
                 <span class="deal-chat-attach-file-name">${escapeHtml(att.file.name)}</span>
               </div>`;
          return `
            <div class="deal-chat-attach-item">
              ${inner}
              <button class="deal-chat-attach-remove" type="button"
                      data-action="chat-attach-remove" data-attachment-index="${i}"
                      aria-label="제거">×</button>
            </div>`;
        }).join('')}</div>`
      : '';

    const existingMsgsHtml = allMessages.length === 0
      ? (chatSending
          ? ''
          : '<p class="text-muted text-sm" style="padding: 8px;">아직 메시지 없음. 아래 입력으로 LLM 에게 명령하세요.</p>')
      : allMessages.map((m) => `
          <div class="deal-chat-msg deal-chat-msg-${m.role}">
            <div class="deal-chat-msg-header">
              <span>${renderMsgSender(m)}</span>
              <span class="text-muted text-xs">${escapeHtml(fmtDateTime(m.created_at))}</span>
            </div>
            <div class="deal-chat-msg-content">${escapeHtml(m.content)}</div>
            ${renderMsgAttachments(m)}
          </div>
        `).join('');

    // 11-4: optimistic user row — 전송 직후 즉시 user 메시지 표시
    //   chatPending 있으면 그 내용 + 첨부 미리보기 함께 그림.
    //   부모 refetch 로 진짜 메시지 들어오면 chatPending = null.
    const pendingUserRow = chatPending
      ? (() => {
          const attsHtml = chatPending.attachments.length > 0
            ? `<div class="deal-chat-msg-attachments">${chatPending.attachments.map((a) => {
                if (a.isImage && a.previewUrl) {
                  return `<img src="${a.previewUrl}" alt="${escapeHtml(a.name)}"
                               class="deal-chat-msg-attachment-image" loading="lazy" />`;
                }
                return `<span class="deal-chat-msg-attachment-link">📎 ${escapeHtml(a.name)}</span>`;
              }).join('')}</div>`
            : '';
          const contentHtml = chatPending.content
            ? `<div class="deal-chat-msg-content">${escapeHtml(chatPending.content)}</div>`
            : '';
          return `
            <div class="deal-chat-msg deal-chat-msg-user deal-chat-msg-pending-user">
              <div class="deal-chat-msg-header">
                <span>나</span>
                <span class="text-muted text-xs">${escapeHtml(fmtDateTime(chatPending.at))}</span>
              </div>
              ${contentHtml}
              ${attsHtml}
            </div>`;
        })()
      : '';

    // 11-4: 로딩 메시지 단순화 — spinner + "답변 생성중..." 만 (시간 안내 제거)
    const pendingBotRow = chatSending
      ? `<div class="deal-chat-msg deal-chat-msg-assistant deal-chat-msg-pending">
           <div class="deal-chat-msg-content deal-chat-msg-loading">
             <span class="deal-chat-spinner" aria-hidden="true"></span>
             <span class="text-muted">답변 생성중...</span>
           </div>
         </div>`
      : '';

    const msgsHtml = existingMsgsHtml + pendingUserRow + pendingBotRow;

    return `
        <h3>채팅</h3>
        <div class="deal-chat-list">${msgsHtml}</div>
        ${chatError ? `<div class="alert alert-error" style="margin: 4px 8px;">${escapeHtml(chatError)}</div>` : ''}
        ${attsPreview}
        <footer class="deal-chat-input">
          <button class="deal-chat-attach-btn" type="button"
                  data-action="chat-attach" aria-label="이미지 첨부"
                  ${chatSending ? 'disabled' : ''}>📎</button>
          <textarea class="deal-chat-input-box"
                 data-action="chat-input"
                 rows="1"
                 placeholder="${chatSending ? '답변 생성중...' : 'Ctrl+V 이미지복붙 / Shift+Enter 줄바꿈)'}"
                 ${chatSending ? 'disabled' : ''}>${escapeHtml(chatInput)}</textarea>
          <button class="btn btn-primary btn-sm" type="button"
                  data-action="chat-send"
                  ${sendDisabled ? 'disabled' : ''}>
            ${chatSending ? '…' : '전송'}
          </button>
          <input type="file"
                 accept="image/*,.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                 multiple
                 data-action="chat-file-input"
                 style="display: none;"/>
        </footer>
    `;
  }

  // ─── 이벤트 핸들러 ──────────────────────────────────────────────
  //
  // 입력 시: state 만 업데이트 (re-render X — focus 보존)
  // Enter 키 / [전송] 버튼: handleChatSend
  handleChatInputChange(e: Event): void {
    const target = e.target as HTMLTextAreaElement;
    this.data.chatInput = target.value;
    this.autoGrow(target);          // 내용에 따라 height 자동 증가 (max 후 스크롤)
    // re-render 안 함 — textarea 안의 focus 끊김 회피.
    // button disabled 만 DOM 직접 toggle (즉시 갱신)
    const btn = this._rootElement?.querySelector<HTMLButtonElement>('[data-action="chat-send"]');
    if (btn) {
      btn.disabled = this.data.chatSending || !target.value.trim();
    }
  }

  handleChatKeydown(e: Event): void {
    const ev = e as KeyboardEvent;
    if (ev.key !== 'Enter') return;
    // 한글 등 IME 조합 중 Enter 는 "조합 확정" — 전송/줄바꿈 모두 양보(반쪽 전송 방지).
    if (ev.isComposing || (ev as KeyboardEvent & { keyCode?: number }).keyCode === 229) return;

    const ta = ev.target as HTMLTextAreaElement;
    if (ev.shiftKey) {
      // Shift+Enter → 줄바꿈을 커서 위치에 직접 삽입(브라우저/IME 편차 없이 항상 동작).
      ev.preventDefault();
      const s  = ta.selectionStart ?? ta.value.length;
      const en = ta.selectionEnd   ?? ta.value.length;
      ta.value = `${ta.value.slice(0, s)}\n${ta.value.slice(en)}`;
      ta.selectionStart = ta.selectionEnd = s + 1;
      this.data.chatInput = ta.value;
      this.autoGrow(ta);
      return;
    }
    // 단독 Enter → 전송
    ev.preventDefault();
    void this.handleChatSend();
  }

  /** textarea 높이를 내용에 맞춰 조절 — max-height(CSS) 넘으면 내부 스크롤. */
  private autoGrow(ta: HTMLTextAreaElement): void {
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }

  /** re-render(refreshInner)/마운트 후 textarea 높이 복원 — 여러 줄 초안 유지. */
  private syncTextareaHeight(): void {
    const ta = this._rootElement?.querySelector<HTMLTextAreaElement>('.deal-chat-input-box');
    if (ta) this.autoGrow(ta);
  }

  // ─── 채팅 첨부 (Step 7) ────────────────────────────────────────
  //
  // (a) Ctrl+V paste 이벤트 — clipboardData 의 image/* 자동 추가
  // (b) [📎] 버튼 클릭 → file input.click() — 파일 선택
  // (c) file input change → state 에 추가 + 미리보기

  async handleChatPaste(e: Event): Promise<void> {
    const ev = e as ClipboardEvent;
    const items = ev.clipboardData?.items;
    if (!items) return;

    const newFiles: api.ChatAttachmentFile[] = [];
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          const previewUrl = await this.readAsDataUrl(file);
          newFiles.push({ file, previewUrl });
        }
      }
    }

    if (newFiles.length > 0) {
      ev.preventDefault();    // 텍스트 paste 방지
      this.data.chatAttachments = [...this.data.chatAttachments, ...newFiles];
      this.refreshInner();
    }
    // 이미지 없으면 텍스트 paste 그대로 진행 (기본 동작)
  }

  handleChatAttachClick(): void {
    // [📎] 버튼 클릭 → 숨겨진 file input.click()
    const input = this._rootElement?.querySelector<HTMLInputElement>('[data-action="chat-file-input"]');
    input?.click();
  }

  async handleChatFileSelect(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;

    // image OR Excel/CSV 둘 다 받음 (10-4). 다른 mime 은 일단 skip.
    const newFiles: api.ChatAttachmentFile[] = [];
    for (const file of files) {
      const name = file.name.toLowerCase();
      const isImage = file.type.startsWith('image/');
      const isExcel =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'text/csv' ||
        file.type === 'application/csv' ||
        // 빈 mime 케이스 — 확장자 추정
        (file.type === '' && /\.(xlsx|xls|csv)$/i.test(name));

      if (isImage) {
        const previewUrl = await this.readAsDataUrl(file);
        newFiles.push({ file, previewUrl });
      } else if (isExcel) {
        // Excel/CSV — 미리보기 없음 (아이콘 + 파일명 표시), previewUrl 은 빈 string
        newFiles.push({ file, previewUrl: '' });
      }
      // 다른 mime → skip (다음 라운드 PDF 등)
    }
    this.data.chatAttachments = [...this.data.chatAttachments, ...newFiles];
    input.value = '';    // 같은 파일 다시 선택 가능하게
    this.refreshInner();
  }

  handleChatAttachRemove(e: Event): void {
    const target = e.target as HTMLElement;
    const idx = Number(target.dataset.attachmentIndex);
    if (!Number.isFinite(idx)) return;
    this.data.chatAttachments = this.data.chatAttachments.filter((_, i) => i !== idx);
    this.refreshInner();
  }

  private readAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error ?? new Error('FileReader 실패'));
      reader.readAsDataURL(file);
    });
  }

  async handleChatSend(): Promise<void> {
    const content = this.data.chatInput.trim();
    const atts    = this.data.chatAttachments;
    // 텍스트 또는 첨부 중 하나는 있어야
    if ((!content && atts.length === 0) || this.data.chatSending) return;

    // 11-4: optimistic — 사용자 메시지를 즉시 UI 에 표시 (API 응답 안 기다림)
    const pendingAttachments = atts.map((a) => ({
      name:       a.file.name,
      isImage:    a.file.type.startsWith('image/'),
      previewUrl: a.previewUrl,         // 이미지면 dataUrl, Excel/CSV 면 빈 string
    }));
    this.data.chatPending = {
      content,
      attachments: pendingAttachments,
      at:          new Date().toISOString(),
    };

    // 보낸 직후 즉시 input/attachments clear + sending=true → spinner row 표시
    const sendAtts = atts;                       // 복사 — refresh 후 비울 거
    this.data.chatInput       = '';
    this.data.chatAttachments = [];
    this.data.chatSending     = true;
    this.data.chatError       = '';
    this.refreshInner();

    try {
      await this.opts.send(content, sendAtts);

      // 부모 refetch (진짜 user+assistant 메시지 수신 + 딜 데이터 갱신).
      // 완료까지 spinner 유지 — 기존 동작과 동일.
      await this.opts.onSent?.();

      this.data.chatSending = false;
      this.data.chatPending = null;             // 진짜 메시지 들어왔으니 pending 제거
      this.refreshInner();
    } catch (err) {
      // 상세 trace 출력 — 브라우저 콘솔 + UI alert
      const e = err as Error & {
        cause?: Error;
        metadata?: Record<string, unknown>;
        code?: string;
      };
      console.error('[chat send error]', {
        message:  e.message,
        name:     e.name,
        code:     e.code,
        metadata: e.metadata,
        cause: e.cause ? {
          message: e.cause.message,
          name:    e.cause.name,
        } : undefined,
        stack: e.stack?.split('\n').slice(0, 5).join('\n'),
      });
      const causeMsg = e.cause?.message ? ` (cause: ${e.cause.message})` : '';
      this.data.chatError   = `${e.message}${causeMsg}`;
      this.data.chatSending = false;
      this.data.chatPending = null;             // 실패해도 pending 정리 (에러 메시지로 안내)
      this.refreshInner();
    }
  }
}
